/** The mobile preview view (`MobilePreviewView`): a record page's phone preview where it is
 * drawn — the dock's Preview segment, or the narrow layout's drawer (the records' preview
 * panes: `RecordPreviewPane`, `TourPreviewPane`). No frame of its own — it fills the box it
 * is put in, and the phone scales to its width and to the height left in the scrolling box.
 * Its state (`useMobilePreview`) is owned by the page, so it can be shown in the dock or the
 * drawer without losing the render; it keeps the last good render while the next one runs.
 *
 * Rendering walks a real serializer and applies the draft through the write funnel and
 * rolls it back, so it happens when the editor asks for it: Render is the primary action,
 * and a fingerprint says when what is on screen is stale. Render is not a save, and the
 * view says so. Every state — rendering, failed (with Retry), locked by a concurrent save
 * (with Retry), nothing to preview, out of date, a question to answer first (`blocked`:
 * Render waits; the server's own 400 `required_choice` is that state, never a failure) —
 * has an icon and words.
 *
 * The phone sits in `PreviewErrorBoundary` (C27): a renderer that throws shows "The preview
 * couldn't render this" with Retry in the phone's place, never a broken page. Which port drew
 * it ("Android port · <commit> · synced <date>") is the tooltip of a focusable info icon beside
 * the device select (information, not a button) — not a footer line under the phone.
 */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockClockOutlinedIcon from '@mui/icons-material/LockClockOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import UpdateIcon from '@mui/icons-material/Update';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { memo, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import {
  isNotPreviewable,
  isPreviewLocked,
  requiredChoiceOf,
  type MobilePreviewResult,
  type PreviewEntity,
} from '@/api/mobilePreview';
import { ConsoleApiError } from '@/api/client';

import { DeviceFrame } from './DeviceFrame';
import { DEFAULT_DEVICE_ID, DEVICE_PROFILES, deviceById } from './deviceProfiles';
import { PreviewErrorBoundary } from './PreviewErrorBoundary';
import { getRenderer } from './rendererRegistry';
import './renderers/android'; // registers the Android renderer
import type { MobilePreviewController } from './useMobilePreview';

/** Under the phone: the box's bottom padding. */
const STAGE_BOTTOM_RESERVE_PX = 16;

/** The nearest ancestor that scrolls vertically (the dock's body, the drawer's). */
function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const { overflowY } = getComputedStyle(p);
    if (overflowY === 'auto' || overflowY === 'scroll') return p;
  }
  return null;
}

/** What Render does, in words — it is not a save. */
export const RENDER_DESCRIPTION = 'Runs your unsaved draft on the server and rolls it back. Nothing is saved.';
/** What Render does with no draft to show. */
export const RENDER_SAVED_DESCRIPTION = 'Redraws the saved version. Nothing is saved.';
/** The provenance tooltip's second line: why the port's sync date matters (help story 6.8). */
export const PROVENANCE_DRIFT = 'Drift from the app is checked by hand: if this date looks old, treat fine detail with suspicion.';

export interface MobilePreviewViewProps {
  controller: MobilePreviewController;
  /** The current draft's fingerprint: a render taken of another one is out of date. */
  fingerprint: string;
  /** There is a draft Render would apply (not merely the permission to draft): the copy
   *  says what Render does — runs the draft and rolls it back, or redraws the saved
   *  version — and that it is not a save. */
  hasDraft?: boolean;
  /** A tour: the day the phone shows (0-based). */
  dayIndex?: number;
  /** A renderer screen other than the payload's default (`Renderer.screens`). */
  screen?: string;
  onScreen?: (screen: string) => void;
  /** The screen is fixed to the default here, for this reason (shown under the select,
   *  which is disabled) — the tour's Itinerary tab, whose phone follows the selected day. */
  screenLocked?: string;
  /** Under the controls: what this render cannot show, and why. */
  notes?: ReactNode;
  /** Why Render cannot run the draft yet — a question the draft must answer first, in the
   *  ledger's words (`Answer “Re-key import matching”`): Render waits, disabled, and the
   *  status line says so, instead of a request the server would refuse. */
  blocked?: string | null;
}

/** The status line and Render's tooltip while a question holds the render. */
export function blockedRenderText(blocked: string): string {
  return `${blocked} to render your draft`;
}

export function MobilePreviewView({
  controller,
  fingerprint,
  hasDraft = false,
  dayIndex,
  screen: chosenScreen,
  onScreen,
  screenLocked,
  notes,
  blocked = null,
}: MobilePreviewViewProps) {
  const screen = screenLocked ? undefined : chosenScreen;
  const [deviceId, setDeviceId] = useState(DEFAULT_DEVICE_ID);
  const device = deviceById(deviceId);
  const { result, renderedFp, pending, error: failure, run } = controller;

  const stale = renderedFp !== null && renderedFp !== fingerprint;

  // The phone fits the height left in the scrolling box below it as well as the width, so
  // the followed day is in view without scrolling the dock.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [maxHeightPx, setMaxHeightPx] = useState<number | undefined>(undefined);
  const hasStage = result !== null;
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const scroller = scrollParent(el);
    if (!scroller) return undefined;
    const measure = () => {
      const top = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
      const room = Math.floor(scroller.clientHeight - top - STAGE_BOTTOM_RESERVE_PX);
      setMaxHeightPx(room > 0 ? room : undefined);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(scroller);
    if (rootRef.current) ro.observe(rootRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [hasStage]);

  const renderer = getRenderer('android');
  // Neither a lock conflict nor "nothing to preview" is a failure — nor a question the server
  // asks first (400 `required_choice`): the page holds Render for its answer (`blocked`).
  const locked = isPreviewLocked(failure);
  const notPreviewable = isNotPreviewable(failure);
  const asksFirst = requiredChoiceOf(failure) !== null;
  const explained = notPreviewable && failure instanceof ConsoleApiError ? failure.message : null;
  const error =
    locked || notPreviewable || asksFirst || !failure
      ? null
      : failure instanceof ConsoleApiError
        ? failure.message
        : 'Preview render failed.';
  const screens = result && renderer?.screens ? renderer.screens(result.api_type) : [];

  const renderSays = blocked ? blockedRenderText(blocked) : hasDraft ? RENDER_DESCRIPTION : RENDER_SAVED_DESCRIPTION;
  const renderButton = (
    <Tooltip title={renderSays}>
      <span>
        <Button
          size="small"
          variant={!blocked && (stale || (!result && !pending)) ? 'contained' : 'outlined'}
          startIcon={<RefreshIcon />}
          onClick={run}
          disabled={pending || blocked !== null}
          aria-description={renderSays}
          data-preview-render
        >
          Render
        </Button>
      </span>
    </Tooltip>
  );

  const deviceSelect = (
    <FormControl size="small" sx={{ flex: 'none', width: 112 }}>
      <InputLabel id="mobile-preview-device">Device</InputLabel>
      <Select
        labelId="mobile-preview-device"
        label="Device"
        value={deviceId}
        onChange={(e) => setDeviceId(e.target.value)}
        renderValue={(v: string) => `${deviceById(v).widthDp} dp`}
      >
        {DEVICE_PROFILES.map((d) => (
          <MenuItem key={d.id} value={d.id}>
            {d.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // Only a failure is an assertive `alert`; what the render says about itself (nothing to
  // preview, a lock, its warnings) is a polite `status`.
  const alerts = (
    <>
      {error && (
        <Alert
          severity="error"
          icon={<ErrorOutlineIcon fontSize="inherit" />}
          action={
            <Button color="inherit" size="small" onClick={run} disabled={pending || blocked !== null}>
              Retry
            </Button>
          }
          data-preview-state="error"
        >
          {/^preview render failed/i.test(error) ? error : `The preview failed: ${error}`}
        </Alert>
      )}
      {explained && (
        <Alert severity="info" role="status" icon={<InfoOutlinedIcon fontSize="inherit" />} data-preview-state="not-previewable">
          {explained}
        </Alert>
      )}
      {locked && (
        <Alert
          severity="info"
          role="status"
          icon={<LockClockOutlinedIcon fontSize="inherit" />}
          action={
            <Button color="inherit" size="small" onClick={run} disabled={pending || blocked !== null}>
              Retry
            </Button>
          }
          data-preview-state="locked"
        >
          Someone else is saving this record right now, so the preview did not run. Retry in a moment.
        </Alert>
      )}
      {result?.warnings.map((w) => (
        <Alert
          key={w.code}
          role="status"
          severity={w.code === 'schema_refused' || w.code === 'pins_not_applied' ? 'warning' : 'info'}
          sx={{ py: 0 }}
        >
          {w.detail}
        </Alert>
      ))}
    </>
  );

  const provenanceText = renderer
    ? `${renderer.label} port · ${renderer.provenance.commit.slice(0, 9)} · synced ${renderer.provenance.syncedAt}`
    : null;
  // Information, not an action: a focusable icon (a tab stop that opens the tooltip on focus),
  // never a button that does nothing when pressed. Its name says which port drew the phone;
  // the tooltip, its description, adds why the date matters.
  const provenance = provenanceText && (
    <Tooltip
      describeChild
      title={
        <>
          {provenanceText}
          <br />
          {PROVENANCE_DRIFT}
        </>
      }
    >
      <Box
        component="span"
        role="img"
        tabIndex={0}
        aria-label={`Drawn by: ${provenanceText}`}
        data-preview-provenance
        sx={{
          flex: 'none',
          display: 'inline-flex',
          p: 0.5,
          borderRadius: 1,
          color: 'text.secondary',
          '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: 0 },
        }}
      >
        <InfoOutlinedIcon fontSize="small" />
      </Box>
    </Tooltip>
  );

  // A new render clears a crash; Retry draws the same one again.
  const stage = result && (
    <PreviewErrorBoundary resetKey={result}>
      <PreviewStage
        result={result}
        device={device}
        renderer={renderer}
        dayIndex={dayIndex}
        screen={screen}
        maxHeightPx={maxHeightPx}
      />
    </PreviewErrorBoundary>
  );

  return (
    <Box ref={rootRef} data-preview-pane="embedded" data-preview-stale={stale || undefined} sx={{ display: 'grid', gap: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 32 }}>
        {/* Polite: a render's outcome is announced without taking the focus. */}
        <Box aria-live="polite" aria-atomic="true" data-preview-status-live sx={{ flex: 1, minWidth: 0 }}>
          <RenderStatus
            pending={pending}
            stale={stale}
            result={result}
            entity={controller.entity}
            notPreviewable={notPreviewable}
            hasDraft={hasDraft}
            blocked={blocked}
          />
        </Box>
        {renderButton}
      </Box>
      {/* Always there, two lines reserved: the copy changes with the draft, the layout does not. */}
      <Typography
        variant="caption"
        color="text.secondary"
        data-preview-render-note={hasDraft ? 'draft' : 'saved'}
        sx={{ minHeight: '3.32em', display: 'block' }}
      >
        {hasDraft
          ? 'Render shows your unsaved changes — it runs them on the server and rolls them back. Nothing is saved.'
          : 'Render redraws the saved version. Nothing is saved.'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {screens.length > 1 && onScreen ? (
          <FormControl size="small" disabled={Boolean(screenLocked)} sx={{ flex: 1, minWidth: 0 }}>
            <InputLabel id="mobile-preview-screen">Screen</InputLabel>
            <Select
              labelId="mobile-preview-screen"
              label="Screen"
              value={screen ?? screens[0]!.id}
              onChange={(e) => onScreen(e.target.value)}
              {...(screenLocked ? { 'aria-describedby': 'mobile-preview-screen-locked' } : {})}
            >
              {screens.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}
        {deviceSelect}
        {provenance}
      </Box>
      {screenLocked && screens.length > 1 && (
        <Typography id="mobile-preview-screen-locked" variant="caption" color="text.secondary" sx={{ mt: -0.5 }} data-preview-screen-locked>
          {screenLocked}
        </Typography>
      )}
      {/* fixed-height slot so the layout never jumps mid-render */}
      <Box sx={{ height: 4 }}>{pending && <LinearProgress aria-label="Rendering the preview" />}</Box>
      {alerts}
      {notes}
      <Box ref={stageRef} data-preview-stage sx={{ minWidth: 0 }}>
        {stage}
      </Box>
    </Box>
  );
}

/** "Out of date": amber outline and `warning.dark` words, with an icon (Icon-And-Words). */
function OutOfDateChip() {
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<UpdateIcon />}
      label="Out of date"
      data-preview-out-of-date
      sx={{ color: 'warning.dark', borderColor: 'warning.main', '& .MuiChip-icon': { color: 'warning.dark' } }}
    />
  );
}

/** What the phone shows now, in words: rendering; a question to answer before Render can run
 *  the draft; nothing to preview; not rendered yet (and what Render will show); the saved
 *  version or the draft — with the Out of date chip beside those words when the draft moved
 *  on. */
function RenderStatus({
  pending,
  stale,
  result,
  entity,
  notPreviewable,
  hasDraft,
  blocked,
}: {
  pending: boolean;
  stale: boolean;
  result: MobilePreviewResult | null;
  entity: PreviewEntity;
  notPreviewable: boolean;
  hasDraft: boolean;
  blocked: string | null;
}) {
  const line = (icon: ReactNode, text: string, state: string, extra?: ReactNode) => (
    <Typography
      variant="body2"
      component="div"
      data-preview-status={state}
      sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 0.75, rowGap: 0.5, '& > svg': { fontSize: 18, flex: 'none' } }}
    >
      {icon}
      <Box component="span" sx={{ minWidth: 0 }}>
        {text}
      </Box>
      {extra}
    </Typography>
  );
  if (pending) return line(<CircularProgress size={16} aria-hidden />, 'Rendering…', 'rendering');
  if (blocked) {
    return line(
      <HelpOutlineIcon aria-hidden sx={{ color: 'warning.dark' }} />,
      blockedRenderText(blocked),
      'needs-answer',
      stale ? <OutOfDateChip /> : undefined,
    );
  }
  if (notPreviewable) return line(<InfoOutlinedIcon aria-hidden sx={{ color: 'info.main' }} />, 'Nothing to preview', 'not-previewable');
  // On demand only: until Render, the pane says what Render will show.
  if (!result) {
    const what = hasDraft ? 'your unsaved draft' : `the saved ${entity}`;
    return line(<HourglassEmptyIcon aria-hidden sx={{ color: 'text.secondary' }} />, `Not rendered yet — Render shows ${what}`, 'none');
  }
  const chip = stale ? <OutOfDateChip /> : undefined;
  return result.source === 'draft'
    ? line(<EditNoteOutlinedIcon aria-hidden sx={{ color: 'warning.dark' }} />, 'Your unsaved draft', stale ? 'draft-stale' : 'draft', chip)
    : line(<CheckCircleOutlineIcon aria-hidden sx={{ color: 'text.secondary' }} />, 'The saved version', stale ? 'saved-stale' : 'saved', chip);
}

/** The rendered phone. Memoised on the render result: the pane re-renders on every
 *  keystroke of the editor beside it (its draft fingerprint drives "Out of date"), and
 *  without this boundary each keystroke re-ran the renderer — re-adapting the payload,
 *  re-rendering the frame's own React root and re-fitting the map. */
const PreviewStage = memo(function PreviewStage({
  result,
  device,
  renderer,
  dayIndex,
  screen,
  maxHeightPx,
}: {
  result: MobilePreviewResult;
  device: ReturnType<typeof deviceById>;
  renderer: ReturnType<typeof getRenderer>;
  dayIndex?: number | undefined;
  screen?: string | undefined;
  maxHeightPx?: number | undefined;
}) {
  if (!renderer || !renderer.supports(result.api_type)) {
    return (
      <Alert severity="warning">
        No renderer for <code>{result.api_type}</code> yet.
      </Alert>
    );
  }
  const imagesBase = String(result.meta.images_base ?? '');
  // A renderer with a Shell owns the frame itself — the tour screen needs layers outside
  // it for the map. See types.ts `Renderer.Shell`.
  return renderer.Shell ? (
    <renderer.Shell
      payload={result.payload}
      apiType={result.api_type}
      device={device}
      imagesBase={imagesBase}
      meta={result.meta}
      styles={renderer.styles}
      {...(dayIndex !== undefined ? { dayIndex } : {})}
      {...(screen !== undefined ? { screen } : {})}
      {...(maxHeightPx !== undefined ? { maxHeightPx } : {})}
    />
  ) : (
    <DeviceFrame device={device} styles={renderer.styles} {...(maxHeightPx !== undefined ? { maxHeightPx } : {})}>
      <renderer.Screen payload={result.payload} apiType={result.api_type} device={device} imagesBase={imagesBase} meta={result.meta} />
    </DeviceFrame>
  );
});
